import MidiEffect from './MidiEffect.js';
import StepData from 'pattern/StepData.js';
import MidiTrigger from './MidiTrigger.js';
import {getTriggerTimes} from 'keyboard/TriggerTimeHelper.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import Resolution from 'engine/Resolution.js';
import {UP, DOWN, UP_DOWN, DOWN_UP} from './constants.js';
import Tone from 'tone';
const TIME_32N = new Tone.Time('32n');

class ChordRhythmEffect extends MidiEffect {
    constructor(track) {
        super('Chord Rhythm', track);

        this.durationFactorParameter =  new AbstractParameter(
            'durationFactor', this, (o, x) => [],
            {
                defaultValue: 1,
                min: 0,
                max: 2,
                unit: ''
            });

        this.registerParameter(this.durationFactorParameter);

        this.oddDelayTimeParameter =  new AbstractParameter(
            'oddDelayTime', this, (o, x) => [],
            {
                defaultValue: 1,
                min: 0.01,
                max: 16,
                unit: ''
            });

        this.registerParameter(this.oddDelayTimeParameter);

        this.evenDelayTimeParameter =  new AbstractParameter(
            'evenDelayTime', this, (o, x) => [],
            {
                defaultValue: 1,
                min: 0.01,
                max: 16,
                unit: ''
            });

        this.registerParameter(this.evenDelayTimeParameter);

        this.delayTimeFactorParameter =  new AbstractParameter(
            'delayTimeFactor', this, (o, x) => [],
            {
                defaultValue: 1,
                min: 0.01,
                max: 4,
                unit: ''
            });

        this.registerParameter(this.delayTimeFactorParameter);

        this.evenVelocityParameter =  new AbstractParameter(
            'evenVelocity', this, (o, x) => [],
            {
                defaultValue: 1,
                min: 0.01,
                max: 1,
                unit: ''
            });

        this.registerParameter(this.evenVelocityParameter);

        this.oddVelocityParameter =  new AbstractParameter(
            'oddVelocity', this, (o, x) => [],
            {
                defaultValue: 1,
                min: 0.01,
                max: 1,
                unit: ''
            });

        this.registerParameter(this.oddVelocityParameter);

        this.repeatsParameter =  new AbstractParameter(
            'repeats', this, (o, x) => [],
            {
                defaultValue: 1,
                min: 1,
                max: 8,
                unit: ''
            });

        this.registerParameter(this.repeatsParameter);

        this.syncedParameter =  new AbstractParameter(
            'synced', this, (o, x) => [],
            {
                defaultValue: true,
                min: false,
                max:true,
                unit: ''
            });

        this.registerParameter(this.syncedParameter);

        this.velocitySpreadParameter =  new AbstractParameter(
            'velocitySpread', this, (o, x) => [],
            {
                defaultValue: 50,
                min: 0,
                max: 100,
                unit: ''
            });

        this.registerParameter(this.velocitySpreadParameter);

        this.velocityParameter =  new AbstractParameter(
            'velocity', this, (o, x) => [],
            {
                defaultValue: 1,
                min: 0,
                max: 1,
                unit: ''
            });

        this.registerParameter(this.velocityParameter);

        this.delays = [];
        this.liveTriggers = [];
    }

    onNewStep() {
        this.triggers = [];
    }

    removeNote(note, sliceNumber, skip=!this.gated) {
        this.delays = this.delays.filter(
            d => d.trigger.stepData.transpose !== note);
    }

    input(trigger, isLive=false) {
        if (!isLive) {
            super.input(trigger);
        } else {
            this.liveTriggers.push(trigger);
        }
    }

    clear() {
        this.triggers = [];
        this.delays = [];
    }

    get oddDelayTime() {
        return this.oddDelayTimeParameter.get();
    }

    get synced() {
        return this.syncedParameter.get();
    }

    get repeats() {
        return this.repeatsParameter.get();
    }

    get evenDelayTime() {
        return this.evenDelayTimeParameter.get();
    }

    get durationFactor() {
        return this.durationFactorParameter.get();
    }

    get delayTimeFactor() {
        return this.delayTimeFactorParameter.get();
    }

    get velocitySpread() {
        return this.velocitySpreadParameter.get() / 100.0;
    }

    get oddVelocity() {
        return Math.max(0, Math.sqrt(this.velocitySpread) - 0.2);
    }

    get velocity() {
        return this.velocityParameter.get();
    }

    get evenVelocity() {
        let otherHalf = 1 - this.velocitySpread;
        return Math.max(0, Math.sqrt(otherHalf) - 0.2);;
    }

    getDelayTrigger(trigger, time, isLive=false) {
        let offsetTime = trigger.time - time;
        let stepOffset = offsetTime / TIME_32N;
        stepOffset = isLive ? 0 : stepOffset;
        return {
            stepNumber: trigger.stepNumber,
            stepOffset: stepOffset,
            trigger: this.copyTrigger(trigger),
            duration: isLive ? undefined : trigger.stepData.duration,
            repeats: 1,
            elapsed: 0,
            even: true,
        };
    }

    copyTrigger(trigger) {
        let stepData = this.getStep(trigger.stepData, undefined, undefined, false);
        return new MidiTrigger(stepData, trigger.time, trigger.stepNumber, trigger.outputNumber);
    }

    output(time, stepNumber, actualStepNumber) {
        // go through the trigger
        for (let i in this.triggers) {
            let trigger = this.triggers[i];
            this.delays.push(this.getDelayTrigger(trigger, time));
        }

        for (let i in this.liveTriggers) {
            let trigger = this.liveTriggers[i];
            trigger.stepNumber = stepNumber;
            this.delays.push(this.getDelayTrigger(trigger, time, true));
        }

        let triggers = [];

        let syncedStepNumber = undefined;
        let syncedStepOffset  = undefined;
        // now go through the delays
        for (let i in this.delays) {
            let delay = this.delays[i];

            let isEven = delay.repeats / this.repeats % 2 == 0;
            let delayTime = isEven ?
                this.evenDelayTime :
                this.oddDelayTime;

            delayTime *= this.delayTimeFactor;

            let velocity = isEven ?
                this.evenVelocity :
                this.oddVelocity;

            velocity *= this.velocity;

            if (delay.elapsed === 0 ||
                this.shouldPlayDelay(
                    delay,
                    stepNumber,
                    delay.isEven ? this.evenDelayTime : this.oddDelayTime))
            {
                // add copied trigger to trigger list
                let timesToPlay = this.getTimesToPlay(delay, time, delayTime);
                let copiedTriggers = timesToPlay.map(
                    x => this.getTriggerToPlay(
                        delay.trigger,
                        x.timeToPlay,
                        stepNumber,
                        velocity,
                        delayTime));
                triggers = [... triggers, ...copiedTriggers];

                syncedStepNumber = stepNumber;
                syncedStepOffset = timesToPlay[timesToPlay.length - 1].offset;

                delay.isEven = isEven;
                delay.stepNumber = stepNumber;
                delay.stepOffset = syncedStepOffset;
                delay.repeats++;
            }

            delay.elapsed++;
        }

        if (this.synced && syncedStepNumber !== undefined) {
            this.delays.forEach(delay => {
                delay.stepNumber = syncedStepNumber;
                delay.stepOffset = syncedStepOffset;
            });
        }

        this.delays = this.delays.filter(
            d => d.duration === undefined ||
                d.elapsed < d.duration * 2);

        this.liveTriggers = [];

        return triggers;
    }

    getTriggerToPlay(trigger, timeToPlay, stepNumber, velocity, delayTime) {
        let copiedTrigger = this.copyTrigger(trigger);
        copiedTrigger.time = timeToPlay;
        copiedTrigger.stepData.duration = this.durationFactor * (delayTime / 2);
        copiedTrigger.stepNumber = stepNumber;
        copiedTrigger.stepData.velocity *= velocity;
        return copiedTrigger;
    }
 

    shouldPlayDelay(delay, stepNumber, delayTime) {
        let lastPlayedStepNumber = (delay.stepNumber + delay.stepOffset) % this.patternLength;
        let minBound = stepNumber;
        let maxBound = stepNumber + 1;

        let nextPlayStepNumber = (lastPlayedStepNumber + delayTime) % this.patternLength;

        return minBound <= nextPlayStepNumber && nextPlayStepNumber < maxBound;
    }

    getTimesToPlay(delay, time, delayTime) {
        let lastPlayedStepNumber = (delay.stepNumber + delay.stepOffset) % this.patternLength;
        let nextPlayStepNumber = (lastPlayedStepNumber + delayTime) % this.patternLength;
        let offset = nextPlayStepNumber - Math.floor(nextPlayStepNumber);
        let times = [];
        for (;offset < 1; offset += delayTime) {
            times.push({
                timeToPlay: time + offset * TIME_32N,
                offset: offset
            });
        }
        return times;
    }

    get patternLength() {
        return this.track.length;
    }

    // need to call addNote when live triggering
    isRealTime() {
        return false;
    }

    getCopy(track, ignoreId=true) {
        let copy = new ChordRhythmEffect(track);
        copy.fromJson(this.getJson(), ignoreId);
        return copy;
    }
}

export default ChordRhythmEffect;
